/* ═══════════════════════════════════════════════════════════════
   moduleInsertPlacement — the pure "where does an Insert land?" decision
   for the Modules toolbox's Insert module… dialog (plan F3a).

   Rule: a SINGLE-chunk module goes right after the block the editor caret
   is in, provided that block sits inside a Part. A caret outside every Part
   (above PART 1, or no caret block) — and every multi-chunk module — keeps
   the existing anchor match (`matchAnchor`: the END of the matching Part;
   "needs a spot" chip when it cannot decide).

   Zero React, zero ProseMirror — the hook feeds it plain blocks + refs.
   ═══════════════════════════════════════════════════════════════ */

import type { ContentBlock } from '../../shared/types';
import type { NumberedRef } from '../../shared/body-layout';
import { matchAnchor, partAtBoundary } from '../../shared/spec-modules';
import type { AnchorMatch, ModuleChunk } from '../../shared/spec-modules';
import type { HeadingMergeTarget } from '../../shared/spec-assemble';

export interface CaretInsertInput {
  /** Non-empty chunks the module will stamp. */
  chunkCount: number;
  /** Top-level block the editor caret is inside, when there is one. */
  caretBlockId?: string | null;
  blocks: ContentBlock[];
  refs: NumberedRef[];
}

/** The block to insert AFTER, or null ⇒ fall back to the anchor match. */
export function caretInsertTarget({
  chunkCount,
  caretBlockId,
  blocks,
  refs,
}: CaretInsertInput): string | null {
  if (chunkCount !== 1 || !caretBlockId) return null;
  const idx = blocks.findIndex((b) => b.id === caretBlockId);
  if (idx < 0) return null;
  // Part number of the caret block itself (0 = before any Part).
  return partAtBoundary(blocks, refs, idx + 1) > 0 ? caretBlockId : null;
}

/* ═══════════════════════════════════════════════════════════════
   planModuleInsert — Insert module… (Josh 2026-10-26, TWEAKS item 13).

   A module insert IGNORES the caret. Each article lands in the spec PART
   with the same number as the module part it was authored in:
     1. a same-heading article IN THAT PART takes its children (the
        heading-merge rule, `findHeadingMergeTarget` — same part only; part
        headings are level 1 and never match) — the caller has already
        resolved that into `merge`;
     2. otherwise the article is appended at the END of that part;
     3. a part the spec lacks is CREATED (standard label, in order — every
        missing part up to it, because parts are counted by position) at the
        end of the document, and the article goes under it;
     4. an article with NO part (the builder's "Unassigned") keeps the caret
        rule (`caretInsertTarget`), then the anchor match, then a chip.
   A legacy captured chunk (real ancestor path) keeps its anchor match, minus
   the caret. Pure — the hook feeds it plain blocks + refs.
   ═══════════════════════════════════════════════════════════════ */

/** The standard heading text of a created part — the forma prints the
 *  "PART n -" prefix, exactly like a brand-new master (SpecLibrary). */
export const STANDARD_PART_TEXT: Record<number, string> = {
  1: 'GENERAL',
  2: 'PRODUCTS',
  3: 'EXECUTION',
};

export interface PlanChunk {
  chunk: ModuleChunk;
  stamped: ContentBlock[];
  merge: HeadingMergeTarget | null;
}

export interface PlannedDrop {
  blocks: ContentBlock[];
  afterBlockId?: string;
  atIndex?: number;
  outsideGroups: true;
}

export interface ModuleInsertPlan {
  drops: PlannedDrop[];
  /** Chunks nothing could place — they become "place me" chips. */
  waiting: Array<{ item: PlanChunk; match: AnchorMatch }>;
  /** Part numbers this insert creates (headings, not module content). */
  createdParts: number[];
  /** Ids of the created part headings (not stamped — they are the spec's). */
  createdHeadingIds: string[];
}

export interface PlanInput {
  items: PlanChunk[];
  blocks: ContentBlock[];
  refs: NumberedRef[];
  /** Unassigned articles only. Null/undefined ⇒ no caret. */
  caretBlockId?: string | null;
  /** Mints a block id for a created part heading. */
  mintId: () => string;
}

/** How many numbered PARTS (level-1 blocks the numbering brain printed a
 *  prefix for) the spec has — the same count `matchAnchor` walks. */
export function numberedPartCount(blocks: ContentBlock[], refs: NumberedRef[]): number {
  const refById = new Map(refs.map((r) => [r.id, r]));
  return blocks.filter((b) => (b.level || 1) <= 1 && !!refById.get(b.id)?.prefix).length;
}

export function planModuleInsert({
  items,
  blocks,
  refs,
  caretBlockId,
  mintId,
}: PlanInput): ModuleInsertPlan {
  const drops: PlannedDrop[] = [];
  const waiting: ModuleInsertPlan['waiting'] = [];
  const partCount = numberedPartCount(blocks, refs);
  const toCreate = new Map<number, ContentBlock[]>();

  const unassigned = items.filter((it) => !it.merge && !((it.chunk.anchor?.partIndex ?? 0) > 0));
  const caretAfter = caretInsertTarget({
    chunkCount: unassigned.length,
    caretBlockId,
    blocks,
    refs,
  });

  for (const item of items) {
    const { chunk, stamped, merge } = item;
    if (merge) {
      drops.push({ blocks: stamped, afterBlockId: merge.insertAfterBlockId, outsideGroups: true });
      continue;
    }
    const part = chunk.anchor?.partIndex ?? 0;
    const hasPath = !!chunk.anchor?.path?.length;

    if (part > 0 && !hasPath) {
      if (part <= partCount) {
        // An empty path = "the end of Part N" (matchAnchor's part landing).
        const match = matchAnchor({ level: 2, path: [], partIndex: part }, blocks, refs);
        if (match.kind === 'unique') {
          drops.push({
            blocks: stamped,
            afterBlockId: match.insertAfterBlockId,
            outsideGroups: true,
          });
        } else waiting.push({ item, match });
      } else if (partCount > 0) {
        toCreate.set(part, [...(toCreate.get(part) || []), ...stamped]);
      } else {
        // No numbered parts at all — nothing to measure a position from.
        waiting.push({ item, match: { kind: 'none' } });
      }
      continue;
    }

    if (part <= 0 && caretAfter) {
      drops.push({ blocks: stamped, afterBlockId: caretAfter, outsideGroups: true });
      continue;
    }
    const match = matchAnchor(chunk.anchor, blocks, refs);
    if (match.kind === 'unique') {
      drops.push({ blocks: stamped, afterBlockId: match.insertAfterBlockId, outsideGroups: true });
    } else waiting.push({ item, match });
  }

  const createdParts: number[] = [];
  const createdHeadingIds: string[] = [];
  if (toCreate.size) {
    const top = Math.max(...toCreate.keys());
    const tail: ContentBlock[] = [];
    for (let p = partCount + 1; p <= top; p++) {
      const id = mintId();
      createdParts.push(p);
      createdHeadingIds.push(id);
      tail.push({ id, level: 1, text: STANDARD_PART_TEXT[p] || `PART ${p}` } as ContentBlock);
      tail.push(...(toCreate.get(p) || []));
    }
    // Authored LAST so anything else landing at the document's end (the end
    // of the spec's last existing part) stays above the new parts.
    const last = blocks[blocks.length - 1];
    drops.push(
      last
        ? { blocks: tail, afterBlockId: last.id, outsideGroups: true }
        : { blocks: tail, atIndex: 0, outsideGroups: true }
    );
  }

  return { drops, waiting, createdParts, createdHeadingIds };
}
